import { runPlaybackAttempt } from "./playback-fallback";

interface CloudflareStreamPlayer {
  currentTime: number;
  duration: number;
  paused: boolean;
  play: () => Promise<void>;
  pause: () => void;
}

type CloudflareStreamFactory = (iframe: HTMLIFrameElement) => CloudflareStreamPlayer;

declare global {
  interface Window {
    Stream?: CloudflareStreamFactory;
  }
}

const sdkUrl = "https://embed.cloudflarestream.com/embed/sdk.latest.js";
let sdkPromise: Promise<CloudflareStreamFactory> | null = null;
const players = new WeakMap<HTMLIFrameElement, CloudflareStreamPlayer>();

const playerFor = (iframe: HTMLIFrameElement, factory: CloudflareStreamFactory): CloudflareStreamPlayer => {
  const existing = players.get(iframe);
  if (existing) return existing;
  const player = factory(iframe);
  players.set(iframe, player);
  return player;
};

const streamFactory = async (): Promise<CloudflareStreamFactory> => {
  if (window.Stream) return window.Stream;
  if (!sdkPromise) {
    sdkPromise = new Promise<CloudflareStreamFactory>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = sdkUrl;
      script.async = true;
      script.addEventListener("load", () => {
        if (window.Stream) resolve(window.Stream);
        else reject(new Error("The video player controls are unavailable."));
      }, { once: true });
      script.addEventListener("error", () => reject(new Error("The video player controls could not be loaded.")), { once: true });
      document.head.append(script);
    }).catch((error: unknown) => {
      sdkPromise = null;
      throw error;
    });
  }
  return sdkPromise;
};

export const prepareStreamPlayer = async (iframe: HTMLIFrameElement): Promise<void> => {
  playerFor(iframe, await streamFactory());
};

export const seekAndPlayStream = (
  iframe: HTMLIFrameElement,
  startMs: number,
  playbackRejected?: () => void,
): boolean => {
  if (!window.Stream) return false;
  const player = playerFor(iframe, window.Stream);
  player.currentTime = startMs / 1_000;
  runPlaybackAttempt(() => player.play(), () => playbackRejected?.());
  return true;
};

export const toggleStreamPlayback = async (iframe: HTMLIFrameElement): Promise<void> => {
  const player = playerFor(iframe, await streamFactory());
  if (player.paused) {
    await player.play();
  } else {
    player.pause();
  }
};

export const adjustStreamPlayback = async (iframe: HTMLIFrameElement, offsetMs: number): Promise<void> => {
  const player = playerFor(iframe, await streamFactory());
  const currentTime = Number(player.currentTime);
  const duration = Number(player.duration);
  const requestedTime = (Number.isFinite(currentTime) ? currentTime : 0) + offsetMs / 1_000;
  player.currentTime = Number.isFinite(duration) && duration >= 0
    ? Math.min(duration, Math.max(0, requestedTime))
    : Math.max(0, requestedTime);
};

export const currentStreamPositionMs = async (iframe: HTMLIFrameElement): Promise<number> => {
  const player = playerFor(iframe, await streamFactory());
  const currentTime = Number(player.currentTime);
  if (!Number.isFinite(currentTime) || currentTime < 0) {
    throw new Error("The current video position is not available yet.");
  }
  return Math.round(currentTime * 1_000);
};
